import React, { useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";

const ROW_HEIGHT = 36;
const OVERSCAN = 15;

const VirtualizedDataTable = ({
  data,
  columns: columnDefs,
  getRowClassName,
  minRows = 15,
  onMouseEnter,
  onMouseLeave,
}) => {
  const parentRef = useRef(null);

  const getHeaderClassName = (col) => col.headerClassName || "data-table__head__column";
  const paddingRowCount = Math.max(0, minRows - data.length);

  if (data.length === 0) {
    return (
      <div
        className="data-table__container"
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div className="data-table__scroll">
          <table>
            <thead>
              <tr>
                {columnDefs.map((col) => (
                  <th key={col.id} className={getHeaderClassName(col)}>
                    {col.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: minRows }, (_, i) => (
                <tr key={i} className="data-table__row">
                  <td colSpan={columnDefs.length} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <VirtualizedBody
      parentRef={parentRef}
      data={data}
      columnDefs={columnDefs}
      getRowClassName={getRowClassName}
      getHeaderClassName={getHeaderClassName}
      paddingRowCount={paddingRowCount}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    />
  );
};

const isTestEnv = typeof process !== "undefined" && process.env.NODE_ENV === "test";

const VirtualizedBody = ({
  parentRef,
  data,
  columnDefs,
  getRowClassName,
  getHeaderClassName,
  paddingRowCount,
  onMouseEnter,
  onMouseLeave,
}) => {
  const rowVirtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: OVERSCAN,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  const rowsToRender =
    virtualItems.length > 0
      ? virtualItems
      : isTestEnv
        ? data.slice(0, 50).map((_, i) => ({
            index: i,
            start: i * ROW_HEIGHT,
            size: ROW_HEIGHT,
            end: (i + 1) * ROW_HEIGHT,
          }))
        : [];

  return (
    <div className="data-table__container" onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <div
        ref={parentRef}
        className="data-table__scroll"
        style={{ maxHeight: "70vh", overflowY: "auto" }}
      >
        <table style={{ width: "100%" }}>
          <thead>
            <tr>
              {columnDefs.map((col) => (
                <th key={col.id} className={getHeaderClassName(col)}>
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rowsToRender.length > 0 && (
              <>
                {rowsToRender[0].start > 0 && (
                  <tr style={{ height: `${rowsToRender[0].start}px` }}>
                    <td colSpan={columnDefs.length} />
                  </tr>
                )}
                {rowsToRender.map((virtualRow) => {
                  const row = data[virtualRow.index];
                  return (
                    <tr
                      key={virtualRow.index}
                      className={getRowClassName ? getRowClassName(row) : "data-table__row"}
                      style={{ height: `${virtualRow.size}px` }}
                    >
                      {columnDefs.map((col) => (
                        <React.Fragment key={col.id}>
                          {col.cell(row, virtualRow.index)}
                        </React.Fragment>
                      ))}
                    </tr>
                  );
                })}
                {virtualItems.length > 0 &&
                  totalSize - virtualItems[virtualItems.length - 1].end > 0 && (
                    <tr
                      style={{
                        height: `${totalSize - virtualItems[virtualItems.length - 1].end}px`,
                      }}
                    >
                      <td colSpan={columnDefs.length} />
                    </tr>
                  )}
                {paddingRowCount > 0 &&
                  Array.from({ length: paddingRowCount }, (_, i) => (
                    <tr key={`padding-${i}`} className="data-table__row">
                      <td colSpan={columnDefs.length} />
                    </tr>
                  ))}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default VirtualizedDataTable;
